import {
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Questionnaire } from './pages/Questionnaire';
import { MainLayout } from './components/MainLayout';
import { Confirmation } from './pages/offboarding/med-g/Confirmation';
import { DirectConfirmationPage } from './pages/offboarding/DirectConfirmationPage';
import { AdditionalDocumentUploadPage } from './pages/offboarding/AdditionalDocumentUploadPage';
import { EntryRouter } from './components/EntryRouter';
import {
  Unsubscribe,
  UnsubscribeConfirm,
  UnsubscribeValidation,
} from './pages/unsubscribe';
import { PatientSatisfactionSurveyPage } from './pages/patientSatisfaction/PatientSatisfactionSurveyPage';
import { PatientSatisfactionUnsubscribePage } from './pages/patientSatisfaction/PatientSatisfactionUnsubscribePage';
import { InvitationRoutes } from './pages/middleware/InvitationRoutes';
import { IntroductionRedirect } from './pages/IntroductionRedirect';
import { QuestionnaireRoutes } from './pages/middleware/QuestionnaireRoutes';
import { RequirePatient } from './pages/middleware/RequirePatient';
import { RequirePatientData } from './pages/middleware/RequirePatientData';
import { Referrer } from './pages/middleware/Referrer';

import { InvitationAuthentication } from './pages/onboarding/med-g/InvitationAuthentication';
import { LandingProfessionalSelect } from './pages/onboarding/landing/LandingProfessionalSelect';
import { LandingPractice } from './pages/onboarding/landing/LandingPractice';
import { LandingSommeilBligny } from './pages/onboarding/LandingSommeilBligny';
import { LandingEtpSommeil } from './pages/onboarding/etp-sommeil/LandingEtpSommeil';
import { LandingPromArs } from './pages/onboarding/prom-ars/LandingPromArs';
import { ResearchConsent } from './pages/offboarding/med-g/ResearchConsent';
import { PatientChecklist } from './pages/offboarding/med-g/PatientChecklist';
import { ErrorPage } from './components/ErrorPage';
import { PatientInformation } from './pages/onboarding/med-g/PatientInformation';
import { PatientAdministrative } from './pages/onboarding/med-g/PatientAdministrative';
import { PatientContact } from './pages/onboarding/med-g/PatientContact';
import { PatientAddress } from './pages/onboarding/med-g/PatientAddress';
import { PatientProfession } from './pages/onboarding/med-g/PatientProfession';
import { ConsultationTime } from './pages/onboarding/med-g/ConsultationTime';
import { SelfIdentificationQuestion } from './pages/onboarding/med-g/SelfIdentificationQuestion';
import { useSynchronizeKB } from './hooks/useSynchronizeKB';
import { useReferrer } from './hooks/useReferrer';
import { useQuestionnaireVariant } from './hooks/useQuestionnaireVariant';
import { WhyPrepareConsultation } from './pages/onboarding/med-g/WhyPrepareConsultation';
import { QuestionnaireStepsOverview } from './pages/onboarding/med-g/QuestionnaireStepsOverview';
import {
  SHARED_ROUTES,
  MED_G_ROUTES,
  SOMMEIL_BLIGNY_ROUTES,
  SOMMEIL_ROUTES,
  MON_BILAN_PREVENTION_ROUTES,
} from './routes';

{
  /* SOMMEIL Bligny Onboarding Routes */
}
import { AccueilPage } from './pages/onboarding/sommeil-bligny/AccueilPage';
import { BlignyIntro1Page } from './pages/onboarding/sommeil-bligny/Intro1Page';
import { BlignyIntro2Page } from './pages/onboarding/sommeil-bligny/Intro2Page';
import { RechercheCliniquePage } from './pages/onboarding/sommeil-bligny/RechercheCliniquePage';

{
  /* MON_BILAN_PREVENTION Onboarding Routes */
}
import { MBPIntro1Page } from './pages/onboarding/mbp/Intro1';
import { MBPIntro2Page } from './pages/onboarding/mbp/Intro2';
import { IneligiblePage } from './pages/onboarding/mbp/Ineligible';

import { useSelector } from 'react-redux';
import { UnavailableQuestionnairePage } from './pages/onboarding/UnavailableQuestionnairePage';
import { FullScreenLoader } from './components/FullScreenLoader';
import { RootState } from './types/graph';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const LandingPageRedirect = () => {
  const practice = useSelector(
    (state: RootState) => state.consultation?.practice,
  );

  if (!practice) {
    return <UnavailableQuestionnairePage />;
  }

  return <FullScreenLoader />;
};

const EntryRoute = ({
  entryType,
}: {
  entryType: 'invitation' | 'practice' | 'professional' | 'assisted';
}) => {
  const { slug, consultationId } = useParams<{
    slug: string;
    consultationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const queryParams = Object.fromEntries(searchParams.entries());
  return (
    <EntryRouter
      entryType={entryType}
      slug={slug}
      consultationId={consultationId}
      queryParams={queryParams}
    />
  );
};

export const AppRoutes = () => {
  const { loading } = useSynchronizeKB();
  useReferrer();
  useQuestionnaireVariant();

  return (
    <SentryRoutes>
      <Route element={<MainLayout headerVariant="landing" />}>
        <Route element={<QuestionnaireRoutes page="INTRODUCTION" />}>
          <Route
            path={MED_G_ROUTES.INTRODUCTION}
            element={<IntroductionRedirect />}
          />
          <Route path="/:slug" element={<EntryRoute entryType="practice" />} />
          <Route
            path="/practice"
            element={<EntryRoute entryType="practice" />}
          />
          <Route
            path="/professional/:slug?"
            element={<EntryRoute entryType="professional" />}
          />
          <Route
            path="/assisted-questionnaire/:consultationId"
            element={<EntryRoute entryType="assisted" />}
          />
          <Route
            path="/p/:slug"
            element={
              <Referrer referrer="sms">
                <EntryRoute entryType="professional" />
              </Referrer>
            }
          />
          <Route
            path={SHARED_ROUTES.INVITATION_AUTHENTICATION}
            element={<InvitationAuthentication />}
          />
          <Route
            path={SHARED_ROUTES.LANDING_PAGE}
            element={<LandingPageRedirect />}
          />
          <Route
            path={SHARED_ROUTES.LANDING_PRACTICE}
            element={<LandingPractice />}
          />
          <Route
            path={SHARED_ROUTES.LANDING_SOMMEIL_BLIGNY}
            element={<LandingSommeilBligny />}
          />
          <Route
            path={SHARED_ROUTES.LANDING_ETP_SOMMEIL}
            element={<LandingEtpSommeil />}
          />
          <Route
            path={SHARED_ROUTES.LANDING_PROM_ARS}
            element={<LandingPromArs />}
          />
          <Route
            path="/invitation/:slug"
            element={<EntryRoute entryType="invitation" />}
          />

          <Route
            path="/i/:slug"
            element={<EntryRoute entryType="invitation" />}
          />
          <Route path="/unsub/:slug" element={<Unsubscribe />} />
          <Route path="/unsub-confirm" element={<UnsubscribeConfirm />} />
          <Route path="/unsub-validation" element={<UnsubscribeValidation />} />
        </Route>

        {/* Outside QuestionnaireRoutes on purpose: it dispatches the page into
            the persisted questionnaire graph, which would rewind an
            in-progress questionnaire to its introduction. */}
        <Route
          path="/patient-satisfaction/:id"
          element={<PatientSatisfactionSurveyPage />}
        />
        <Route
          path="/unsub-patient-satisfaction/:id"
          element={<PatientSatisfactionUnsubscribePage />}
        />
      </Route>

      <Route element={<MainLayout headerVariant="default" />}>
        <Route element={<QuestionnaireRoutes page="INTRODUCTION" />}>
          <Route
            path={SHARED_ROUTES.LANDING_PROFESSIONAL_SELECT}
            element={<LandingProfessionalSelect />}
          />
        </Route>
        <Route
          path={MED_G_ROUTES.WHY_PREPARE_CONSULTATION}
          element={<WhyPrepareConsultation />}
        />
        <Route
          path={SHARED_ROUTES.PATIENT_INFORMATION}
          element={<PatientInformation />}
        />
        <Route element={<RequirePatient />}>
          <Route
            path={SHARED_ROUTES.PATIENT_ADMINISTRATIVE}
            element={<PatientAdministrative />}
          />
          <Route
            path={SHARED_ROUTES.PATIENT_CONTACT}
            element={<PatientContact />}
          />
          <Route
            path={SHARED_ROUTES.PATIENT_ADDRESS}
            element={<PatientAddress />}
          />
          <Route
            path={SHARED_ROUTES.PATIENT_PROFESSION}
            element={<PatientProfession />}
          />
        </Route>
        <Route
          path={SHARED_ROUTES.CONSULTATION_TIME}
          element={<ConsultationTime />}
        />
        <Route
          path={MED_G_ROUTES.QUESTIONNAIRE_STEPS_OVERVIEW}
          element={<QuestionnaireStepsOverview />}
        />
        <Route
          path={SHARED_ROUTES.SELF_IDENTIFICATION_QUESTION}
          element={<SelfIdentificationQuestion />}
        />

        {/* SOMMEIL Onboarding Routes */}
        <Route path={SOMMEIL_BLIGNY_ROUTES.ACCUEIL} element={<AccueilPage />} />
        <Route path={SOMMEIL_ROUTES.INTRO_1} element={<BlignyIntro1Page />} />
        <Route path={SOMMEIL_ROUTES.INTRO_2} element={<BlignyIntro2Page />} />
        <Route
          path={SOMMEIL_BLIGNY_ROUTES.RECHERCHE}
          element={<RechercheCliniquePage />}
        />

        {/* MON_BILAN_PREVENTION Onboarding Routes */}
        <Route
          path={MON_BILAN_PREVENTION_ROUTES.INTRO_1}
          element={<MBPIntro1Page />}
        />
        <Route
          path={MON_BILAN_PREVENTION_ROUTES.INTRO_2}
          element={<MBPIntro2Page />}
        />
        <Route
          path={MON_BILAN_PREVENTION_ROUTES.INELIGIBLE}
          element={<IneligiblePage />}
        />

        <Route element={<InvitationRoutes />} />
      </Route>

      <Route element={<MainLayout headerVariant="questionnaire" />}>
        <Route element={<RequirePatientData />}>
          <Route element={<QuestionnaireRoutes />}>
            <Route
              path="/questionnaire/:page?"
              element={<Questionnaire questionnaireLoading={loading} />}
            />
          </Route>
        </Route>
        <Route element={<QuestionnaireRoutes />}>
          <Route
            index
            element={<Navigate to={SHARED_ROUTES.LANDING_PAGE} replace />}
          />
        </Route>
      </Route>

      <Route element={<MainLayout headerVariant="default" />}>
        <Route
          index
          path={MED_G_ROUTES.CHECKLIST}
          element={<PatientChecklist />}
        />
        <Route
          index
          path={MED_G_ROUTES.RESEARCH_CONSENT}
          element={<ResearchConsent />}
        />
        <Route
          index
          path={MED_G_ROUTES.CONFIRMATION}
          element={<Confirmation />}
        />
        <Route
          path={SHARED_ROUTES.DIRECT_CONFIRMATION}
          element={<DirectConfirmationPage />}
        />
        <Route
          path={SHARED_ROUTES.ADDITIONAL_DOCUMENT_UPLOAD}
          element={<AdditionalDocumentUploadPage />}
        />

        <Route
          path={SHARED_ROUTES.ERROR}
          element={<ErrorPage resetOptions={{ clearStorage: true }} />}
        />
      </Route>
    </SentryRoutes>
  );
};
